import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MdArrowBackIosNew } from 'react-icons/md';
import NavBar from '../../../components/NavBar';
import AVC_BASE_URL from '../../../services/config/avc';

const MIN_IFRAME_HEIGHT = 480;
const LOAD_TIMEOUT_MS = 20000;
const AVC_URL = String(AVC_BASE_URL || '').replace(/\/+$/, '');
const AVC_IFRAME_URLS = {
	'indicadores-angels': `${AVC_URL}/avc/indicadores-angels/`,
	acompanhamento: `${AVC_URL}/avc/acompanhamento/`,
};

function AvcIframePage({ title, page }) {
	const iframeRef = useRef(null);
	const iframeLoadedRef = useRef(false);
	const lastIframeHeightRef = useRef(MIN_IFRAME_HEIGHT);
	const [iframeHeight, setIframeHeight] = useState(MIN_IFRAME_HEIGHT);
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const iframeUrl = AVC_IFRAME_URLS[page];
	const solicitarAtualizacaoAltura = useCallback(() => {
		if (!iframeUrl || !iframeRef.current?.contentWindow) return;

		iframeRef.current.contentWindow.postMessage(
			{ type: 'IFRAME_RESIZE' },
			new URL(iframeUrl).origin,
		);
	}, [iframeUrl]);

	useEffect(() => {
		iframeLoadedRef.current = false;
		lastIframeHeightRef.current = MIN_IFRAME_HEIGHT;
		setIframeHeight(MIN_IFRAME_HEIGHT);
		setIsLoading(true);
		setHasError(false);
	}, [iframeUrl]);

	useEffect(() => {
		if (!iframeUrl) return undefined;

		const avcOrigin = new URL(iframeUrl).origin;
		const timeoutId = window.setTimeout(() => {
			if (!iframeLoadedRef.current) {
				setIsLoading(false);
				setHasError(true);
			}
		}, LOAD_TIMEOUT_MS);

		const handleMessage = (event) => {
			// A altura só pode ser recebida do aplicativo AVC aberto neste iframe.
			if (event.origin !== avcOrigin) return;
			if (event.source !== iframeRef.current?.contentWindow) return;

			const data = event.data || {};
			if (data.type !== 'IFRAME_RESIZE') return;

			const reportedHeight = Number(
				data.height || data.iframeHeight || data.documentHeight || 0,
			);
			if (!Number.isFinite(reportedHeight) || reportedHeight <= 0) return;

			const nextHeight = Math.max(
				MIN_IFRAME_HEIGHT,
				Math.ceil(reportedHeight),
			);
			if (lastIframeHeightRef.current === nextHeight) return;

			lastIframeHeightRef.current = nextHeight;
			setIframeHeight(nextHeight);
			setIsLoading(false);
			setHasError(false);
			window.clearTimeout(timeoutId);
		};

		window.addEventListener('message', handleMessage);
		return () => {
			window.clearTimeout(timeoutId);
			window.removeEventListener('message', handleMessage);
		};
	}, [iframeUrl]);

	const handleLoad = () => {
		iframeLoadedRef.current = true;
		setIsLoading(false);
		setHasError(false);
		solicitarAtualizacaoAltura();
	};

	return (
		<div className='flex min-h-screen flex-col bg-[#F0F4F8] font-inter'>
			<NavBar contentMaxWidthClassName='max-w-[1152.03px]' />

			<header className='mt-[80px] bg-[#F0F4F8] px-6 py-[36px] text-center text-[26px] font-semibold text-[#13335A] sm:text-[28px]'>
				{title}
			</header>

			<div className='border-t border-[#E3E3E3] bg-white'>
				<div className='mx-auto w-[90%] max-w-[1152.03px] py-4 sm:w-[95%]'>
					<Link
						to='/avc'
						className='inline-flex items-center gap-1.5 text-[18px] font-medium text-[#17395F] transition-colors duration-300 hover:text-[#60758F]'
						aria-label='Voltar para Acompanhamento AVC'>
						<MdArrowBackIosNew size={15} />
						Voltar
					</Link>
				</div>
			</div>

			<main className='relative flex-1 bg-white'>
				{isLoading && (
					<div className='absolute inset-0 z-10 flex min-h-[480px] items-center justify-center bg-white'>
						<div
							aria-label='Carregando página AVC'
							className='h-10 w-10 animate-spin rounded-full border-4 border-gray-300 border-t-[#004A80]'
						/>
					</div>
				)}

				{hasError ? (
					<div className='flex min-h-[480px] items-center justify-center px-6 text-center'>
						<p className='max-w-md text-[18px] font-medium text-gray-700'>
							A página não pôde ser carregada. Tente novamente
							mais tarde.
						</p>
					</div>
				) : (
					<iframe
						ref={iframeRef}
						id={`avc-${page}-frame`}
						title={title}
						src={iframeUrl}
						onLoad={handleLoad}
						onFocus={solicitarAtualizacaoAltura}
						style={{
							width: '100%',
							minHeight: `${MIN_IFRAME_HEIGHT}px`,
							height: `${iframeHeight}px`,
							border: 0,
							display: 'block',
						}}
					/>
				)}
			</main>
		</div>
	);
}

export default AvcIframePage;
